import type { CSSProperties, MouseEvent } from 'react';

const CLICK_MOVE_THRESHOLD = 5;

type DetailTableRowEventHandler<T> = (
  event: MouseEvent<HTMLElement>,
  record: T,
) => void;

export type CreateDetailTableRowPropsOptions<T> = {
  handleOpenDetail: (record: T) => void;
  canViewDetail?: boolean;
  className?: string;
  style?: CSSProperties;
  onClick?: DetailTableRowEventHandler<T>;
  onMouseDown?: DetailTableRowEventHandler<T>;
  onMouseMove?: DetailTableRowEventHandler<T>;
};

export function shouldIgnoreTableRowClick(event: MouseEvent<HTMLElement>) {
  const target = event.target;
  if (!(target instanceof HTMLElement)) return false;

  return Boolean(
    target.closest(
      [
        '[data-row-click-ignore]',
        'a',
        'button',
        'input',
        'label',
        'select',
        'textarea',
        '[contenteditable="true"]',
        '[role="button"]',
        '[role="checkbox"]',
      ].join(','),
    ),
  );
}

export function createDetailTableRowProps<T>({
  canViewDetail = true,
  handleOpenDetail,
  className,
  style,
  onClick,
  onMouseDown,
  onMouseMove,
}: CreateDetailTableRowPropsOptions<T>) {
  return (record: T) => {
    const pointerState = {
      moved: false,
      x: 0,
      y: 0,
    };

    return {
      className,
      style: getDetailTableRowStyle(canViewDetail, style),
      onMouseDown: (event: MouseEvent<HTMLElement>) => {
        if (event.button === 0) {
          pointerState.moved = false;
          pointerState.x = event.clientX;
          pointerState.y = event.clientY;
        }
        onMouseDown?.(event, record);
      },
      onMouseMove: (event: MouseEvent<HTMLElement>) => {
        if (
          Math.abs(event.clientX - pointerState.x) > CLICK_MOVE_THRESHOLD ||
          Math.abs(event.clientY - pointerState.y) > CLICK_MOVE_THRESHOLD
        ) {
          pointerState.moved = true;
        }
        onMouseMove?.(event, record);
      },
      onClick: (event: MouseEvent<HTMLElement>) => {
        if (
          pointerState.moved ||
          shouldIgnoreTableRowClick(event) ||
          hasSelectedText()
        ) {
          return;
        }

        onClick?.(event, record);
        if (!canViewDetail || event.defaultPrevented) return;

        handleOpenDetail(record);
      },
    };
  };
}

function getDetailTableRowStyle(
  canViewDetail: boolean,
  style: CSSProperties | undefined,
) {
  if (!canViewDetail) return style;

  return {
    ...style,
    cursor: style?.cursor ?? 'pointer',
  } satisfies CSSProperties;
}

function hasSelectedText() {
  const selection = window.getSelection?.();
  return Boolean(
    selection && !selection.isCollapsed && selection.toString().trim(),
  );
}
